import { supabase } from './config';

// Content Service for Normalized Supabase Database
export const contentService = {
  // Get all website content from normalized tables
  async getContent() {
    try {
      // Fetch all data in parallel
      const [
        companyResult,
        navigationResult,
        pagesResult,
        sectionsResult,
        projectsResult,
        servicesResult,
        careersResult,
        contactResult,
        footerResult
      ] = await Promise.all([
        supabase.from('company_info').select('*').single(),
        supabase.from('navigation_items').select('*').order('display_order'),
        supabase.from('pages').select('*'),
        supabase.from('page_sections').select('*').order('display_order'),
        supabase.from('projects').select('*').order('display_order'),
        supabase.from('service_items').select('*').order('display_order'),
        supabase.from('career_positions').select('*').order('display_order'),
        supabase.from('contact_info').select('*').order('display_order'),
        supabase.from('footer_links').select('*').order('display_order')
      ]);

      // Check for errors
      if (companyResult.error) throw companyResult.error;
      if (navigationResult.error) throw navigationResult.error;

      // Transform data to match the old structure
      const company = companyResult.data;
      const companyYear = company.established_year?.toString() || '2010';
      const navigation = navigationResult.data;
      const pages = pagesResult.data || [];
      const sections = sectionsResult.data || [];
      const projects = projectsResult.data || [];
      const services = servicesResult.data || [];
      const careers = careersResult.data || [];
      const contactInfo = contactResult.data || [];
      const footerLinks = footerResult.data || [];

      // Create page lookup map
      const pageMap = {};
      pages.forEach(p => {
        pageMap[p.slug] = p.id;
      });

      // Build the data structure
      const data = {
        company: {
          name: company.name,
          tagline: company.tagline,
          fullName: company.full_name,
          logo: company.logo,
          since: companyYear,
          description: company.description
        },
        navigation: navigation.map(item => ({
          name: item.name,
          path: item.path
        })),
        home: this.buildHomeData(sections, projects, services, pageMap),
        services: this.buildServicesData(sections, services, pageMap),
        about: this.buildAboutData(sections, pageMap),
        careers: this.buildCareersData(sections, careers, pageMap),
        contact: this.buildContactData(sections, contactInfo, pageMap),
        footer: this.buildFooterData(footerLinks)
      };

      const replaceYear = (value) => {
        if (typeof value !== 'string') return value;
        return value.replace(/2010/g, companyYear);
      };

      data.company.description = replaceYear(data.company.description);
      if (data.home?.hero?.subtitle) data.home.hero.subtitle = replaceYear(data.home.hero.subtitle);
      if (Array.isArray(data.about?.intro?.content)) {
        data.about.intro.content = data.about.intro.content.map(replaceYear);
      }
      if (data.footer?.about?.content) data.footer.about.content = replaceYear(data.footer.about.content);

      return { data };
    } catch (error) {
      console.error('Get content error:', error.message);
      throw error;
    }
  },

  // Build home page data
  buildHomeData(sections, projects, services, pageMap) {
    const homeId = pageMap['home'];
    const homeSections = sections.filter(s => s.page_id === homeId);
    
    const hero = homeSections.find(s => s.section_key === 'hero');
    const aboutPreview = homeSections.find(s => s.section_key === 'aboutPreview');
    const splitSections = homeSections.filter(s => s.section_type === 'split');

    return {
      hero: hero ? {
        title: hero.title,
        subtitle: hero.subtitle,
        backgroundImage: hero.background_image,
        buttons: hero.metadata?.buttons || []
      } : {},
      aboutPreview: aboutPreview ? {
        label: aboutPreview.label,
        title: aboutPreview.title,
        button: aboutPreview.metadata?.button || {}
      } : {},
      sections: splitSections.map(s => ({
        type: 'split',
        reverse: s.reverse,
        image: s.image_url,
        title: s.title,
        content: s.content ? s.content.split('\n\n') : []
      })),
      services: {
        label: 'Our Services',
        title: 'Specialized Cable Industry Services\nFor Your Equipment Needs',
        description: 'Comprehensive packaging solutions for cable manufacturing equipment',
        items: services.map(s => ({
          name: s.name,
          icon: s.icon
        }))
      },
      projects: {
        id: 'projects',
        label: 'Our Clients',
        title: 'Trusted by Leading Cable Manufacturers',
        items: projects.map(p => ({
          title: p.title,
          category: p.category,
          image: p.image_url
        }))
      },
      clients: {
        label: 'Our Clients',
        title: 'Trusted by Leading Cable Manufacturers',
        logos: projects.map(p => ({ name: p.title, url: '' }))
      }
    };
  },

  // Build services page data
  buildServicesData(sections, services, pageMap) {
    const serviceId = pageMap['services'];
    const serviceSections = sections.filter(s => s.page_id === serviceId);
    const heroSec = serviceSections.find(s => s.section_key === 'hero');
    
    return {
      hero: {
        title: heroSec?.title || 'Cable Industry Services',
        backgroundImage: heroSec?.background_image || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80'
      },
      intro: {
        label: 'What We Do',
        title: 'Specialized Packaging Solutions\nfor Cable & Conductor Manufacturing'
      },
      details: serviceSections.filter(s => s.section_type === 'service-detail').map(s => ({
        reverse: s.reverse,
        image: s.image_url,
        title: s.title,
        content: s.content ? [s.content] : [],
        list: s.metadata?.list || null
      })),
      allServices: {
        label: 'Complete Service List',
        title: 'Everything You Need\nUnder One Roof',
        items: services.map(s => ({
          name: s.name,
          description: s.description || ''
        }))
      }
    };
  },

  // Build about page data
  buildAboutData(sections, pageMap) {
    const aboutId = pageMap['about'];
    const aboutSections = sections.filter(s => s.page_id === aboutId);

    const heroSec       = aboutSections.find(s => s.section_key === 'hero');
    const introSec      = aboutSections.find(s => s.section_key === 'intro');
    const missionSec    = aboutSections.find(s => s.section_key === 'mission');
    const valuesSec     = aboutSections.find(s => s.section_key === 'values');
    const whyChooseSec  = aboutSections.find(s => s.section_key === 'whyChooseUs');
    const statsSec      = aboutSections.find(s => s.section_key === 'stats');

    return {
      hero: {
        title: heroSec?.title || 'About Us',
        backgroundImage: heroSec?.background_image || 'https://images.unsplash.com/photo-1605902711834-8b11c3e3ef2f?w=1920&q=80'
      },
      intro: {
        label: introSec?.label || 'Who We Are',
        title: introSec?.title || 'About HASNAT Packaging',
        content: introSec?.content
          ? introSec.content.split('\n\n')
          : [
              'HASNAT Packaging is a specialized packaging solutions provider serving the Cable & Conductor Industry since 2010.',
              'With over 13 years of experience, we have become the trusted partner for leading cable manufacturers across India.'
            ]
      },
      mission: {
        reverse: missionSec?.reverse ?? false,
        image: missionSec?.image_url || 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&q=80',
        title: missionSec?.title || 'Our Mission',
        content: missionSec?.content
          ? missionSec.content.split('\n\n')
          : [
              'To provide world-class packaging solutions that protect and deliver the precision equipment that powers the cable manufacturing industry.',
              'We are committed to quality, reliability, and continuous innovation in industrial packaging.'
            ]
      },
      stats: statsSec?.metadata?.items || [
        { number: '13+', label: 'Years Experience' },
        { number: '10+', label: 'Major Clients' },
        { number: '15+', label: 'Plant Locations Served' },
        { number: '99%', label: 'Client Satisfaction' }
      ],
      values: {
        reverse: valuesSec?.reverse ?? true,
        image: valuesSec?.image_url || 'https://images.unsplash.com/photo-1494412651409-8963ce7935a7?w=1200&q=80',
        title: valuesSec?.title || 'Our Values',
        content: valuesSec?.content
          ? valuesSec.content.split('\n\n')
          : [
              'We believe in integrity, quality, and long-term partnerships with our clients.',
              'Every packaging solution we deliver reflects our commitment to excellence and our clients\' success.'
            ]
      },
      whyChooseUs: {
        label: whyChooseSec?.label || 'Why Choose Us',
        title: whyChooseSec?.title || 'The HASNAT Advantage',
        items: whyChooseSec?.metadata?.items || [
          { title: 'Industry Expertise', description: '13+ years specializing exclusively in cable manufacturing equipment packaging.' },
          { title: 'Custom Solutions', description: 'Tailored packaging designs for every type of cable machinery and component.' },
          { title: 'Quality Assured', description: 'Rigorous quality control ensures your equipment arrives in perfect condition.' },
          { title: 'Pan-India Coverage', description: 'Serving 15+ plant locations across India with reliable logistics.' }
        ]
      }
    };
  },

  // Build careers page data
  buildCareersData(sections, careers, pageMap) {
    const careerId = pageMap['careers'];
    const careerSections = sections.filter(s => s.page_id === careerId);

    const heroSec     = careerSections.find(s => s.section_key === 'hero');
    const introSec    = careerSections.find(s => s.section_key === 'intro');
    const benefitsSec = careerSections.find(s => s.section_key === 'benefits');
    const cultureSec  = careerSections.find(s => s.section_key === 'culture');
    const appSec      = careerSections.find(s => s.section_key === 'application');

    return {
      hero: {
        title: heroSec?.title || 'Join Our Team',
        backgroundImage: heroSec?.background_image || 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920&q=80'
      },
      intro: {
        label: introSec?.label || 'Build Your Career',
        title: introSec?.title || 'Why Work With Us?',
        content: introSec?.content || 'At HASNAT Packaging, we believe our people are our greatest asset. We offer a dynamic work environment, competitive compensation, and opportunities to grow alongside an industry leader.'
      },
      benefits: {
        title: benefitsSec?.title || 'Employee Benefits',
        items: benefitsSec?.metadata?.items || [
          'Competitive salary and performance bonuses',
          'Comprehensive health insurance coverage',
          'Retirement savings plan with company match',
          'Professional development and training programs',
          'Paid time off and flexible work arrangements'
        ]
      },
      culture: {
        reverse: cultureSec?.reverse ?? false,
        image: cultureSec?.image_url || 'https://images.unsplash.com/photo-1605902711834-8b11c3e3ef2f?w=1200&q=80',
        title: cultureSec?.title || 'Our Culture',
        content: cultureSec?.content
          ? cultureSec.content.split('\n\n')
          : [
              'We foster a culture of collaboration, innovation, and continuous improvement.',
              'Our diverse team brings together expertise from packaging, logistics, and cable manufacturing to deliver outstanding results.'
            ]
      },
      positions: {
        label: 'Open Positions',
        title: 'Current Opportunities',
        note: 'Click on a position to apply',
        jobs: careers.map(c => ({
          title: c.title,
          type: c.employment_type,
          location: c.location,
          description: c.description,
          status: c.status
        }))
      },
      application: {
        label: appSec?.label || 'Apply Now',
        title: appSec?.title || 'Start Your Journey With Us',
        description: appSec?.content || 'Ready to join our team? Fill out the application form and we\'ll get back to you within 2 business days.'
      }
    };
  },

  // Build contact page data
  buildContactData(sections, contactInfo, pageMap) {
    const contactId = pageMap['contact'];
    const contactSections = sections.filter(s => s.page_id === contactId);
    const heroSec = contactSections.find(s => s.section_key === 'hero');
    return {
      hero: {
        title: heroSec?.title || 'Contact Us',
        backgroundImage: heroSec?.background_image || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80'
      },
      intro: {
        label: 'Get in Touch',
        title: 'Let\'s Discuss Your\nPackaging Needs',
        content: 'Have a question or ready to get started? We\'re here to help.'
      },
      contactInfo: contactInfo.map(c => ({
        icon: c.icon,
        title: c.title,
        content: c.content,
        link: c.link
      })),
      form: {
        title: 'Send Us a Message',
        submitText: 'Send Message',
        fields: [
          { name: 'name',    label: 'Your Name',     type: 'text',     required: true },
          { name: 'email',   label: 'Email Address',  type: 'email',    required: true },
          { name: 'phone',   label: 'Phone Number',   type: 'tel',      required: false },
          { name: 'company', label: 'Company Name',   type: 'text',     required: false },
          { name: 'message', label: 'Message',        type: 'textarea', required: true }
        ]
      }
    };
  },

  // Build footer data
  buildFooterData(footerLinks) {
    const quickLinks = footerLinks.filter(l => l.category === 'quick-links');
    const serviceLinks = footerLinks.filter(l => l.category === 'services');
    const socialLinks = footerLinks.filter(l => l.category === 'social');

    return {
      about: {
        title: 'About HASNAT Packaging',
        content: 'Professional packaging solutions provider since 2010.'
      },
      quickLinks: {
        title: 'Quick Links',
        links: quickLinks.map(l => ({ name: l.name, path: l.path }))
      },
      services: {
        title: 'Services',
        links: serviceLinks.map(l => ({ name: l.name, path: l.path }))
      },
      social: {
        title: 'Follow Us',
        links: socialLinks.map(l => ({ name: l.name, url: l.path, icon: l.name.toLowerCase() }))
      },
      copyright: '© 2026 HASNAT PACKAGING Private Limited. All rights reserved.'
    };
  },

  // Update company info
  async updateCompanyInfo(data) {
    const companyId = (await supabase.from('company_info').select('id').single()).data?.id;
    
    const { error } = await supabase
      .from('company_info')
      .update({
        name: data.name,
        tagline: data.tagline,
        full_name: data.fullName,
        logo: data.logo,
        established_year: parseInt(data.since) || 2010,
        description: data.description
      })
      .eq('id', companyId);

    if (error) throw error;
    return true;
  },

  // Update navigation items
  async updateNavigation(items) {
    // Delete all and re-insert for simplicity
    await supabase.from('navigation_items').delete().gte('display_order', 0);
    
    const { error } = await supabase
      .from('navigation_items')
      .insert(items.map((item, index) => ({
        name: item.name,
        path: item.path,
        display_order: index + 1
      })));

    if (error) throw error;
    return true;
  },

  // Update projects
  async updateProjects(projects) {
    await supabase.from('projects').delete().gte('display_order', 0);
    
    const { error } = await supabase
      .from('projects')
      .insert(projects.map((p, index) => ({
        title: p.title,
        category: p.category,
        image_url: p.image,
        display_order: index + 1
      })));

    if (error) throw error;
    return true;
  },

  // Update services
  async updateServices(services) {
    await supabase.from('service_items').delete().gte('display_order', 0);
    
    const { error } = await supabase
      .from('service_items')
      .insert(services.map((s, index) => ({
        name: s.name,
        icon: s.icon || '',
        description: s.description || '',
        display_order: index + 1
      })));

    if (error) throw error;
    return true;
  },

  // Update careers
  async updateCareers(jobs) {
    await supabase.from('career_positions').delete().gte('display_order', 0);
    
    const { error } = await supabase
      .from('career_positions')
      .insert(jobs.map((j, index) => ({
        title: j.title,
        employment_type: j.type,
        location: j.location,
        description: j.description,
        status: j.status || 'open',
        display_order: index + 1
      })));

    if (error) throw error;
    return true;
  },

  // Update contact info
  async updateContactInfo(items) {
    await supabase.from('contact_info').delete().gte('display_order', 0);

    if (!items || items.length === 0) return true;

    const { error } = await supabase
      .from('contact_info')
      .insert(items.map((c, index) => ({
        info_type: c.icon || 'info',
        icon: c.icon,
        title: c.title,
        content: c.content,
        link: c.link,
        display_order: index + 1
      })));

    if (error) throw error;
    return true;
  },

  // Upsert a single page section by key
  async upsertSection(pageSlug, sectionKey, sectionType, fields) {
    const { data: page } = await supabase
      .from('pages')
      .select('id')
      .eq('slug', pageSlug)
      .single();

    if (!page) {
      console.warn(`upsertSection: page '${pageSlug}' not found in DB — skipping section '${sectionKey}'`);
      return;
    }

    // Try update first, then insert
    const { data: existing } = await supabase
      .from('page_sections')
      .select('id')
      .eq('page_id', page.id)
      .eq('section_key', sectionKey)
      .maybeSingle();

    if (existing) {
      const { error } = await supabase
        .from('page_sections')
        .update({ ...fields })
        .eq('id', existing.id);
      if (error) console.warn(`upsertSection update warning [${pageSlug}/${sectionKey}]:`, error.message);
    } else {
      const { error } = await supabase
        .from('page_sections')
        .insert({ page_id: page.id, section_key: sectionKey, section_type: sectionType, ...fields });
      if (error) console.warn(`upsertSection insert warning [${pageSlug}/${sectionKey}]:`, error.message);
    }
  },

  // Get content backups (last 50 snapshots stored in content_snapshots if available)
  async getBackups() {
    try {
      const { data, error } = await supabase
        .from('content_snapshots')
        .select('id, created_at, label')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        // Table may not exist — return empty list gracefully
        console.warn('Backups not available:', error.message);
        return [];
      }
      return data || [];
    } catch {
      return [];
    }
  },

  // Restore a content backup
  async restoreBackup(backupId) {
    try {
      const { data, error } = await supabase
        .from('content_snapshots')
        .select('snapshot_data')
        .eq('id', backupId)
        .single();

      if (error) throw error;
      if (!data?.snapshot_data) throw new Error('Snapshot data is empty');

      await this.updateContent(data.snapshot_data);
      return data.snapshot_data;
    } catch (error) {
      throw new Error('Restore failed: ' + error.message);
    }
  },

  // Generic update for full content (for CMS compatibility)
  async updateContent(content) {
    try {
      // Update company info
      if (content.company) {
        await this.updateCompanyInfo(content.company);
      }

      // Update navigation
      if (content.navigation) {
        await this.updateNavigation(content.navigation);
      }

      // Update projects / clients
      if (content.home?.projects?.items) {
        await this.updateProjects(content.home.projects.items);
      }

      // Update service items list
      if (content.home?.services?.items) {
        await this.updateServices(content.home.services.items);
      }

      // Update career positions
      if (content.careers?.positions?.jobs) {
        await this.updateCareers(content.careers.positions.jobs);
      }

      // Update contact info
      if (content.contact?.contactInfo) {
        await this.updateContactInfo(content.contact.contactInfo);
      }

      // Persist editable page sections (best-effort — won't fail the whole save)
      // Home hero
      if (content.home?.hero) {
        await this.upsertSection('home', 'hero', 'hero', {
          title: content.home.hero.title,
          subtitle: content.home.hero.subtitle,
          background_image: content.home.hero.backgroundImage,
          metadata: { buttons: content.home.hero.buttons || [] }
        });
      }
      // Home aboutPreview
      if (content.home?.aboutPreview) {
        await this.upsertSection('home', 'aboutPreview', 'about-preview', {
          label: content.home.aboutPreview.label,
          title: content.home.aboutPreview.title,
          metadata: { button: content.home.aboutPreview.button || {} }
        });
      }
      // Home split sections (with images)
      if (content.home?.sections) {
        const { data: homePage } = await supabase.from('pages').select('id').eq('slug', 'home').single();
        if (homePage) {
          await supabase.from('page_sections').delete().eq('page_id', homePage.id).eq('section_type', 'split');
          if (content.home.sections.length > 0) {
            await supabase.from('page_sections').insert(
              content.home.sections.map((s, i) => ({
                page_id: homePage.id,
                section_type: 'split',
                section_key: `split-${i}`,
                title: s.title,
                content: Array.isArray(s.content) ? s.content.join('\n\n') : (s.content || ''),
                image_url: s.image,
                reverse: s.reverse || false,
                display_order: i + 1
              }))
            );
          }
        }
      }
      // Services hero
      if (content.services?.hero) {
        await this.upsertSection('services', 'hero', 'hero', {
          title: content.services.hero.title || 'Cable Industry Services',
          background_image: content.services.hero.backgroundImage
        });
      }
      // Services detail sections (with images)
      if (content.services?.details) {
        const { data: servicesPage } = await supabase.from('pages').select('id').eq('slug', 'services').single();
        if (servicesPage) {
          await supabase.from('page_sections').delete().eq('page_id', servicesPage.id).eq('section_type', 'service-detail');
          if (content.services.details.length > 0) {
            await supabase.from('page_sections').insert(
              content.services.details.map((d, i) => ({
                page_id: servicesPage.id,
                section_type: 'service-detail',
                section_key: `detail-${i}`,
                title: d.title,
                content: Array.isArray(d.content) ? d.content.join('\n\n') : (d.content || ''),
                image_url: d.image,
                reverse: d.reverse || false,
                metadata: { list: d.list || null },
                display_order: i + 1
              }))
            );
          }
        }
      }
      // About hero
      if (content.about?.hero) {
        await this.upsertSection('about', 'hero', 'hero', {
          title: content.about.hero.title || 'About Us',
          background_image: content.about.hero.backgroundImage
        });
      }
      // About intro
      if (content.about?.intro) {
        await this.upsertSection('about', 'intro', 'intro', {
          label: content.about.intro.label,
          title: content.about.intro.title,
          content: Array.isArray(content.about.intro.content)
            ? content.about.intro.content.join('\n\n')
            : content.about.intro.content
        });
      }
      // About stats
      if (content.about?.stats) {
        await this.upsertSection('about', 'stats', 'stats', {
          metadata: { items: content.about.stats }
        });
      }
      // About whyChooseUs
      if (content.about?.whyChooseUs) {
        await this.upsertSection('about', 'whyChooseUs', 'why-choose-us', {
          label: content.about.whyChooseUs.label,
          title: content.about.whyChooseUs.title,
          metadata: { items: content.about.whyChooseUs.items || [] }
        });
      }
      // About mission (with image)
      if (content.about?.mission) {
        await this.upsertSection('about', 'mission', 'split', {
          title: content.about.mission.title,
          content: Array.isArray(content.about.mission.content)
            ? content.about.mission.content.join('\n\n')
            : content.about.mission.content,
          image_url: content.about.mission.image,
          reverse: content.about.mission.reverse || false
        });
      }
      // About values (with image)
      if (content.about?.values) {
        await this.upsertSection('about', 'values', 'split', {
          title: content.about.values.title,
          content: Array.isArray(content.about.values.content)
            ? content.about.values.content.join('\n\n')
            : content.about.values.content,
          image_url: content.about.values.image,
          reverse: content.about.values.reverse ?? true
        });
      }
      // Careers hero
      if (content.careers?.hero) {
        await this.upsertSection('careers', 'hero', 'hero', {
          title: content.careers.hero.title || 'Join Our Team',
          background_image: content.careers.hero.backgroundImage
        });
      }
      // Careers intro
      if (content.careers?.intro) {
        await this.upsertSection('careers', 'intro', 'intro', {
          label: content.careers.intro.label,
          title: content.careers.intro.title,
          content: content.careers.intro.content
        });
      }
      // Careers benefits
      if (content.careers?.benefits) {
        await this.upsertSection('careers', 'benefits', 'benefits', {
          title: content.careers.benefits.title,
          metadata: { items: content.careers.benefits.items || [] }
        });
      }
      // Careers culture (with image)
      if (content.careers?.culture) {
        await this.upsertSection('careers', 'culture', 'split', {
          title: content.careers.culture.title,
          content: Array.isArray(content.careers.culture.content)
            ? content.careers.culture.content.join('\n\n')
            : content.careers.culture.content,
          image_url: content.careers.culture.image,
          reverse: content.careers.culture.reverse || false
        });
      }
      // Contact hero
      if (content.contact?.hero) {
        await this.upsertSection('contact', 'hero', 'hero', {
          title: content.contact.hero.title || 'Contact Us',
          background_image: content.contact.hero.backgroundImage
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Update content error:', error);
      throw error;
    }
  }
};

export default contentService;
